import React, { ChangeEvent, useId, useRef, useState } from 'react';

import { Icon, type IconType } from '@/stories/atoms/Icon/Icon';
import { FieldProps } from '../Input/Input';
import { DropdownButton, DropdownPanel, DropdownWpr } from '../Dropdown/Dropdown';
import { useClickOutside } from '@/hooks/useClickOutside';
import { TypeBien } from '@/stories/organisms/Search/Filters/TypeBien/TypeBien';

import styles from '@/stories/components/Form/Input/input.module.css';
import selectStyles from './custom-select.module.css'
import clsx from 'clsx';

export type Option = {
  value: string,
  label: string
  icon?: IconType
}

export type InputProps = Omit<FieldProps, 'value'> & Omit<React.ComponentProps<'select'>, 'value'> & {
  sizeVariant?: 'small',
  options: Option[],
  item?: 'TypeBien'
  searchable?: boolean,
  value?: {
    label: string,
    value: string
  }
}

const mapping = {
  TypeBien
}

export const CustomSelect = ({
  label,
  id,
  value,
  setValue,
  required: _required,
  helpText,
  errorText,
  inline,
  hideLabel,
  hideOptionnal,
  icon,
  variant,
  sizeVariant,
  searchable,
  options,
  item,
  ...props
}: InputProps) => {
  const [open, setOpen] = useState<boolean>(false)
  const ref = useRef(null)
  useClickOutside(ref, () => setOpen(false), open)
  const generatedId = useId()

  const [selectedIcon, setSelectedIcon] = useState(icon)

  const [list, setList] = useState(options)

  const handleChange = (option: Option) => {
    if (setValue) {
      if (value?.value !== option.value) {
        setValue(option.value)
        setSelectedIcon(option.icon)
      }
       else {
        setValue('')
        setSelectedIcon(icon)
      }
    }
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target
    setList(options.filter(option => option.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())))
  }

  const renderOption = (option: Option) => {
    const {icon, label, value: optionValue, ...itemProps} = option

    if (item) {
      const Component = mapping[item]

      return (
        <Component
          key={label}
          variant={variant}
          id={label + id}
          onClick={() => handleChange(option)}
          label={label}
          icon={icon}
          {...itemProps}/>
      )
    }

    return (
      <button
        key={label}
        type='button'
        id={label + id}
        onClick={() => handleChange(option)}
        className={clsx(selectStyles.option, value?.value === optionValue && selectStyles.selected)}
        {...itemProps}>
          {icon && <Icon icon={icon}/>}
          {label}
        </button>
    )
  }

  return (
    <DropdownWpr open={open} setOpen={setOpen} errorText={errorText} id={id || generatedId} {...props}>
      <DropdownButton open={open} setOpen={setOpen} id={id || generatedId} icon={selectedIcon} variant="secondary" title={
        value && value.value ? <>
          {value.label}
        </> : label
      }/>
      <DropdownPanel open={open && options.length > 0} ref={ref} id={id || generatedId} className={selectStyles.panel}>
        {searchable && (
          <>
            <label htmlFor={generatedId + 'input'} className='sr-only'>Chercher dans la liste</label>
            <input
              id={generatedId + 'input'}
              onChange={handleSearch}
              placeholder='Rechercher'
              className={clsx(styles.input, variant && styles[variant])}/>
          </>
        )}
        <div className={clsx(styles.wrapper, selectStyles.wrapper)}>
          <div className={clsx(selectStyles.list)}>
            {list.map((option: Option) => renderOption(option))}
          </div>
        </div>
      </DropdownPanel>
    </DropdownWpr>
  )
};
